import { useCallback, useState } from "react";
import { HttpStatusCode, isAxiosError } from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LEGAL_COMPLIANCE_QUERY_KEYS } from "@/features/legal/config/legal.config";
import { acceptLegalCompliance, getCurrentLegalCompliance } from "@/features/legal/services/legalCompliance.service";
import type { CurrentLegalCompliance, LegalCompliance } from "@/features/legal/types/legalCompliance.type";

const PRESENTATION_OUTDATED_CODE = "legal_presentation_outdated";

const isConflict = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === HttpStatusCode.Conflict;

// This screen no longer matches what the API expects (new presentation
// version or label): refetching cannot fix it, only a page reload can.
export const isLegalPresentationOutdatedError = (error: unknown): boolean =>
  isConflict(error) && isAxiosError(error) && error.response?.data?.error?.code === PRESENTATION_OUTDATED_CODE;

export const isLegalComplianceOutdatedError = (error: unknown): boolean =>
  isConflict(error) && !isLegalPresentationOutdatedError(error);

export const useLegalCompliance = () => {
  const queryClient = useQueryClient();
  const [isOutdated, setIsOutdated] = useState(false);
  const [isPresentationOutdated, setIsPresentationOutdated] = useState(false);
  const [hasAcceptError, setHasAcceptError] = useState(false);

  const query = useQuery<CurrentLegalCompliance | null>({
    queryKey: LEGAL_COMPLIANCE_QUERY_KEYS.current(),
    queryFn: getCurrentLegalCompliance,
    retry: false,
    refetchOnWindowFocus: false,
    gcTime: 0,
  });

  const acceptMutation = useMutation({
    mutationFn: (legalCompliance: LegalCompliance) => acceptLegalCompliance(legalCompliance),
    onMutate: () => {
      setIsOutdated(false);
      setIsPresentationOutdated(false);
      setHasAcceptError(false);
    },
    onError: (error) => {
      if (isLegalPresentationOutdatedError(error)) {
        setIsPresentationOutdated(true);
        return;
      }
      // Only a stale version warrants a silent refetch: any other failure
      // (incl. the 503 lock timeout) must surface as a generic error instead.
      if (isLegalComplianceOutdatedError(error)) {
        setIsOutdated(true);
        queryClient.invalidateQueries({ queryKey: LEGAL_COMPLIANCE_QUERY_KEYS.current() });
        return;
      }
      setHasAcceptError(true);
    },
  });

  const reload = useCallback(() => {
    setIsOutdated(false);
    setHasAcceptError(false);
    query.refetch();
  }, [query]);

  return {
    legalCompliance: query.data ?? null,
    isLoading: query.isLoading,
    isLoadError: query.isError,
    reload,
    accept: acceptMutation.mutate,
    isAccepting: acceptMutation.isPending,
    isOutdated,
    isPresentationOutdated,
    hasAcceptError,
  };
};
